import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Alert, Box, Paper, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import PageMeta from "../components/common/PageMeta";
import OrderSearchCard from "../components/evidenceBuilder/OrderSearchCard";
import OrderCandidates from "../components/evidenceBuilder/OrderCandidates";
import BankCaseDetails, { NewBankCase } from "../components/evidenceBuilder/BankCaseDetails";
import RecentBankCases from "../components/evidenceBuilder/RecentBankCases";
import { BuilderCard, PageTitle } from "../components/evidenceBuilder/builderUi";
import { useApi } from "../hooks/useApi";
import { useCompanyId } from "../hooks/useCompanyId";
import { useOrderSearch } from "../hooks/useOrderSearch";
import { useStateValue } from "../context/StateValueContext";
import { hasPermission } from "../functions/permissionUtils";
import { PERMISSIONS } from "../constants/permissions";
import { apiErrorMessage } from "../utils/apiError";
import { BankCase, BankCaseListRow, OrderCandidate } from "../types/evidenceBankCase";

const BASE = "/api/evidence-bank-cases";

export default function EvidenceBuilder() {
  const navigate = useNavigate();
  const [{ user, miniCompanies }] = useStateValue();
  const { companyId: pickedCompanyId } = useCompanyId();
  // The top bar's picker is only there for all-companies users; everyone else works in their own company.
  const companyId = hasPermission(user, PERMISSIONS.DISPUTES_ALL_COMPANIES) ? pickedCompanyId : user?.companyId ?? null;
  // Separate instances: a list reload must not abort a search, nor the spinner of one show on the other.
  const { get, loading: listLoading } = useApi();
  const { post: create } = useApi();
  const { searching, searchError, found, findOrder, resetSearch } = useOrderSearch();

  const [picked, setPicked] = useState<OrderCandidate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [cases, setCases] = useState<BankCaseListRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [listError, setListError] = useState<string | null>(null);

  // A company switch starts a new list; a page that answers for the old one is dropped, whichever page it was.
  const listVersion = useRef(0);
  const loadCases = useCallback(async (nextPage: number) => {
    const version = listVersion.current;
    const isCurrent = () => version === listVersion.current;
    try {
      const query = [companyId ? `companyId=${companyId}` : null, `page=${nextPage}`].filter(Boolean).join("&");
      const res = await get<{ cases: BankCaseListRow[]; hasMore: boolean }>(`${BASE}?${query}`);
      if (!isCurrent()) return;
      setCases((shown) => (nextPage === 1 ? res.cases : [...shown, ...res.cases]));
      setHasMore(res.hasMore);
      setPage(nextPage);
      setListError(null);
    } catch (err: any) {
      Sentry.captureException(err);
      if (isCurrent()) setListError(apiErrorMessage(err, "Failed to load the bank cases"));
    }
  }, [get, companyId]);

  // Another company is another search: what was found belongs to the one the agent left.
  useEffect(() => {
    listVersion.current += 1;
    resetSearch();
    setPicked(null);
    setCreateError(null);
    setCases([]);
    setPage(1);
    setHasMore(false);
    void loadCases(1);
  }, [loadCases, resetSearch]);

  const createCase = async (bankCase: NewBankCase) => {
    if (companyId === null) return;
    setSubmitting(true);
    setCreateError(null);
    try {
      const res = await create<{ bankCase: BankCase }>(BASE, { companyId, ...bankCase }, { slowApi: true });
      navigate(`/evidence-builder/${res.bankCase.evidence_bank_case_id}`);
    } catch (err: any) {
      // The company already holds this dispute ID: that case is the one to work on.
      if (err?.status === 409 && err?.data?.duplicateOf) {
        navigate(`/evidence-builder/${err.data.duplicateOf}`, { state: { existingCase: true } });
        return;
      }
      if (err?.status !== 400 && err?.status !== 409) Sentry.captureException(err);
      setCreateError(apiErrorMessage(err, "Failed to create the case"));
    } finally {
      setSubmitting(false);
    }
  };

  const companyName = miniCompanies.find((company) => company.id === companyId)?.name ?? "";

  return (
    <Box sx={{ fontSize: 13, lineHeight: "normal", color: "#1d2939" }}>
      <PageMeta title="Chargemont | Evidence Builder" description="Build evidence for a dispute in a bank's portal" />

      {/* Hidden rather than unmounted: coming back from the details finds the search as it was left. */}
      <Box sx={{ display: picked ? "none" : "flex", flexDirection: "column", gap: "20px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <PageTitle>Evidence builder</PageTitle>
          <Box sx={{ color: "#667085" }}>For a dispute that lives in a bank&apos;s portal and not in ours.</Box>
        </Box>
        {companyId === null ? (
          <Paper sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, px: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Please select a company from the header to search for an order.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "20px", alignItems: { xs: "stretch", md: "flex-start" } }}>
            <Box sx={{ width: { md: 380 }, flexShrink: 0 }}>
              <OrderSearchCard searching={searching} onSearch={(query) => findOrder({ ...query, companyId })} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {found ? (
                <OrderCandidates result={found.result} searchedBy={found.by} onContinue={setPicked} />
              ) : (
                <BuilderCard>
                  {searchError ? <Alert severity="error">{searchError}</Alert> : <Box sx={{ color: "#667085" }}>Search to see the matching orders here.</Box>}
                </BuilderCard>
              )}
            </Box>
          </Box>
        )}
        <RecentBankCases
          cases={cases}
          hasMore={hasMore}
          loading={listLoading}
          error={listError}
          showCompany={companyId === null}
          onMore={() => { void loadCases(page + 1); }}
        />
      </Box>

      {picked && (
        <BankCaseDetails
          candidate={picked}
          companyName={companyName}
          submitting={submitting}
          error={createError}
          onBack={() => { setPicked(null); setCreateError(null); }}
          onSubmit={createCase}
        />
      )}
    </Box>
  );
}
